import type {
  AuthUser,
  ForgotPasswordRequest,
  LoginResponse,
  ResetPasswordRequest,
} from '@repo/shared-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiClientError, apiFetch } from '@/lib/api';
import { currentTenantSlug } from '@/lib/env';
import { toast } from '@/lib/sonner';
import { useAuthStore } from '@/stores/auth.store';

interface SignInInput {
  email: string;
  password: string;
  rememberMe?: boolean;
}

/**
 * Auth mutations. One mutation per server endpoint; each wraps `apiFetch`
 * with the onSuccess/onError semantics specific to that flow.
 *
 *  - useSignin            → POST /auth/signin (pre-auth; ignores /me)
 *  - useSignout           → POST /auth/signout (requires bearer; doesn't 401-redirect)
 *  - useForgotPassword    → POST /auth/forgot-password
 *  - useResetPassword     → POST /auth/reset-password
 *  - useSwitchTenant      → POST /auth/switch-tenant
 *
 * Why useMutation (not useQuery): these mutate state. TanStack Query's
 * mutation lives in `useMutation` and its onSuccess is the seam for
 * side-effects (store writes, cache invalidation, toasts).
 */
export function useSignin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SignInInput): Promise<LoginResponse> => {
      const res = await apiFetch<LoginResponse>('/auth/signin', {
        method: 'POST',
        body: JSON.stringify(input),
        skipAuthRedirect: true,
      });
      return res;
    },
    onSuccess: (data) => {
      useAuthStore.getState().setSession({ token: data.token, user: data.user });
      queryClient.invalidateQueries();
    },
    onError: (err: unknown) => {
      const message =
        err instanceof ApiClientError && err.status === 401
          ? 'Credenciales inválidas'
          : 'No se pudo iniciar sesión';
      toast.error(message);
    },
  });
}

export function useSignout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await apiFetch<{ ok: true }>('/auth/signout', {
        method: 'POST',
        skipAuthRedirect: true,
      });
    },
    onSuccess: () => {
      useAuthStore.getState().clearAuth();
      queryClient.clear();
      toast.info('Sesión cerrada');
    },
    onError: () => {
      // Even if the server-side invalidation fails, we still wipe the
      // local store — the user's intent ("log me out") is what matters.
      useAuthStore.getState().clearAuth();
      queryClient.clear();
    },
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: async (input: ForgotPasswordRequest) => {
      return apiFetch<{ ok: true; token?: string }>('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify(input),
        skipAuthRedirect: true,
      });
    },
    onSuccess: () => {
      toast.success('Si el correo existe, recibirás instrucciones para restablecer la contraseña');
    },
    onError: () => {
      toast.error('No se pudo procesar la solicitud');
    },
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: async (input: ResetPasswordRequest) => {
      return apiFetch<{ ok: true }>('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify(input),
        skipAuthRedirect: true,
      });
    },
    onSuccess: () => {
      toast.success('Tu contraseña fue actualizada');
    },
    onError: (err: unknown) => {
      const message =
        err instanceof ApiClientError && err.status === 401
          ? 'El enlace expiró o ya fue usado'
          : 'No se pudo restablecer la contraseña';
      toast.error(message);
    },
  });
}

export interface SwitchTenantInput {
  tenantSlug: string;
}

/**
 * Switch tenant — issues a new JWT bound to the target tenant.
 *
 * The store IS rewritten with the new token + user but NOT cleared. On
 * success we invalidate every cached query so tenant-scoped reads
 * refetch with the new RLS context on the next navigation.
 *
 * Note: switching to the SAME tenant is a 400 VALIDATION on the server
 * (the service refuses no-op switches). `currentTenantSlug()` is the
 * identity case the component should preempt.
 */
export function useSwitchTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SwitchTenantInput): Promise<LoginResponse> => {
      return apiFetch<LoginResponse>('/auth/switch-tenant', {
        method: 'POST',
        body: JSON.stringify(input),
        skipAuthRedirect: true,
      });
    },
    onSuccess: (data) => {
      useAuthStore.getState().setSession({ token: data.token, user: data.user });
      queryClient.invalidateQueries();
      toast.success(`Sesión activa en ${currentTenantSlug() || 'la organización'}`);
    },
    onError: (err: unknown) => {
      const message =
        err instanceof ApiClientError && err.status === 403
          ? 'No tienes acceso a esa organización'
          : 'No se pudo cambiar de organización';
      toast.error(message);
    },
  });
}

/**
 * Convenience helper — returns the user from the store. Used by
 * components that need the user but not the full /me round-trip
 * (the AuthUser is hydrated from /signin or /me and updated locally).
 */
export function useCurrentUser(): AuthUser | null {
  return useAuthStore((s) => s.user);
}
